import type {
  GiftPassBundle,
  GiftPassBundleCreateRequest,
  GiftPassBundleListResponse,
  GiftPassBundlePatchRequest,
  GiftPassBundlePreviewQuery,
  GiftPassBundlePreviewResponse,
  GiftPassBundleResponse,
  GiftPassBundleSortField,
  GiftPassBundleSortOrder,
} from '@bonsai/shared';

import { apiFetch } from '../lib/apiFetch';

const BASE = '/api/v1/gift-pass-bundles';

/** The most the API returns in one page. */
export const GIFT_PASS_BUNDLE_PAGE_LIMIT = 200;

export class GiftPassBundleApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors: Record<string, string> = {},
  ) {
    super(message);
    this.name = 'GiftPassBundleApiError';
  }
}

async function send(url: string, init?: RequestInit): Promise<Response> {
  const res = await apiFetch(url, init);
  if (res.ok) return res;
  const body = (await res.json().catch(() => ({}))) as {
    message?: string;
    fieldErrors?: Record<string, string>;
  };
  throw new GiftPassBundleApiError(
    res.status,
    body.message ?? `Request failed (${res.status})`,
    body.fieldErrors ?? {},
  );
}

const bundleUrl = (id: string) => `${BASE}/${encodeURIComponent(id)}`;

export interface GiftPassBundleListParams {
  limit?: number;
  offset?: number;
  sort?: GiftPassBundleSortField;
  order?: GiftPassBundleSortOrder;
}

export async function fetchGiftPassBundles(
  params: GiftPassBundleListParams = {},
  signal?: AbortSignal,
): Promise<GiftPassBundleListResponse> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  const query = qs.toString();
  const res = await send(query ? `${BASE}?${query}` : BASE, { signal });
  return (await res.json()) as GiftPassBundleListResponse;
}

export async function fetchGiftPassBundle(
  id: string,
  signal?: AbortSignal,
): Promise<GiftPassBundle> {
  const res = await send(bundleUrl(id), { signal });
  return ((await res.json()) as GiftPassBundleResponse).bundle;
}

export async function createGiftPassBundle(
  body: GiftPassBundleCreateRequest,
): Promise<GiftPassBundle> {
  const res = await send(BASE, { method: 'POST', body: JSON.stringify(body) });
  return ((await res.json()) as GiftPassBundleResponse).bundle;
}

export async function updateGiftPassBundle(
  id: string,
  patch: GiftPassBundlePatchRequest,
): Promise<GiftPassBundle> {
  const res = await send(bundleUrl(id), { method: 'PATCH', body: JSON.stringify(patch) });
  return ((await res.json()) as GiftPassBundleResponse).bundle;
}

export async function fetchGiftPassBundlePreview(
  terms: GiftPassBundlePreviewQuery,
  signal?: AbortSignal,
): Promise<GiftPassBundlePreviewResponse> {
  const qs = new URLSearchParams({
    passCount: String(terms.passCount),
    discountType: terms.discountType,
    discountValue: String(terms.discountValue),
  });
  const res = await send(`${BASE}/preview?${qs.toString()}`, { signal });
  return (await res.json()) as GiftPassBundlePreviewResponse;
}
